const GEMINT_MEDIA_HOSTS = new Set(['media.gemint.io', 'media.dev.gemint.io'])
const OPTIMIZED_IMAGE_PATH_PREFIX = '/i/'
const NON_OPTIMIZABLE_MEDIA_EXTENSIONS = new Set([
  'apng',
  'gif',
  'mp4',
  'mov',
  'webm',
])

type CdnImageOptions = {
  width?: number
  format?: 'auto' | 'webp'
}

type PreloadCdnImageOptions = CdnImageOptions & {
  timeoutMs?: number
  fetchPriority?: 'high' | 'low' | 'auto'
}

type ImageFetchPriority = NonNullable<PreloadCdnImageOptions['fetchPriority']>

type ImagePreloadEntry = {
  image: HTMLImageElement | null
  promise: Promise<void>
  fetchPriority: ImageFetchPriority
}

const imagePreloadEntries = new Map<string, ImagePreloadEntry>()

const IMAGE_FETCH_PRIORITY_RANK: Record<ImageFetchPriority, number> = {
  low: 0,
  auto: 1,
  high: 2,
}

export function getOptimizedCdnImageUrl(
  imageUrl: string | null | undefined,
  options: CdnImageOptions = {},
): string {
  const trimmedUrl = imageUrl?.trim()
  if (!trimmedUrl) return ''

  let url: URL

  try {
    url = new URL(trimmedUrl)
  } catch {
    return trimmedUrl
  }

  if (!GEMINT_MEDIA_HOSTS.has(url.hostname)) {
    return trimmedUrl
  }

  const extension = url.pathname.split('.').pop()?.toLowerCase()
  if (extension && NON_OPTIMIZABLE_MEDIA_EXTENSIONS.has(extension)) {
    return trimmedUrl
  }

  if (!url.pathname.startsWith(OPTIMIZED_IMAGE_PATH_PREFIX)) {
    url.pathname = `${OPTIMIZED_IMAGE_PATH_PREFIX}${url.pathname.replace(/^\/+/, '')}`
  }

  if (options.width && Number.isFinite(options.width) && options.width > 0) {
    url.searchParams.set('w', String(Math.round(options.width)))
  }

  url.searchParams.set('f', options.format ?? 'webp')

  return url.toString()
}

export function preloadOptimizedCdnImage(
  imageUrl: string | null | undefined,
  options: PreloadCdnImageOptions = {},
): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()

  const src = getOptimizedCdnImageUrl(imageUrl, options)
  if (!src) return Promise.resolve()

  const requestedFetchPriority = options.fetchPriority ?? 'auto'
  const cached = imagePreloadEntries.get(src)
  if (cached) {
    if (
      cached.image &&
      IMAGE_FETCH_PRIORITY_RANK[requestedFetchPriority] >
        IMAGE_FETCH_PRIORITY_RANK[cached.fetchPriority]
    ) {
      cached.fetchPriority = requestedFetchPriority
      cached.image.fetchPriority = requestedFetchPriority
    }
    return cached.promise
  }

  let resolvePromise: () => void = () => undefined
  const promise = new Promise<void>((resolve) => {
    resolvePromise = resolve
  })
  const image = new Image()
  const entry: ImagePreloadEntry = {
    image,
    promise,
    fetchPriority: requestedFetchPriority,
  }
  imagePreloadEntries.set(src, entry)

  let settled = false
  let timeout: number | undefined

  const resolveOnce = () => {
    if (settled) return
    settled = true
    if (timeout) window.clearTimeout(timeout)
    entry.image = null
    resolvePromise()
  }

  const decodeAndResolve = () => {
    if (typeof image.decode !== 'function') {
      resolveOnce()
      return
    }

    image.decode().then(resolveOnce).catch(resolveOnce)
  }

  image.onload = decodeAndResolve
  image.onerror = resolveOnce
  image.fetchPriority = requestedFetchPriority

  if (options.timeoutMs && options.timeoutMs > 0) {
    timeout = window.setTimeout(resolveOnce, options.timeoutMs)
  }

  image.src = src

  if (image.complete) decodeAndResolve()

  return promise
}

export function preloadOptimizedCdnImages(
  imageUrls: Array<string | null | undefined>,
  options: PreloadCdnImageOptions = {},
): Promise<void> {
  const uniqueUrls = Array.from(new Set(imageUrls.filter(Boolean)))

  return Promise.allSettled(
    uniqueUrls.map((imageUrl) => preloadOptimizedCdnImage(imageUrl, options)),
  ).then(() => undefined)
}
